import { skipToken } from '@reduxjs/toolkit/query';
import { compact, uniq } from 'lodash';
import { useAsync } from 'react-use';
import { type AsyncState } from 'react-use/lib/useAsync';

import { getDataSourceSrv } from '@grafana/runtime';
import { type DataQuery } from '@grafana/schema';
import { useGetDisplayMappingQuery } from 'app/api/clients/iam/v0alpha1';
import { extractErrorMessage } from 'app/api/utils';
import { contextSrv } from 'app/core/services/context_srv';
import { type SavedQuery, type SavedQueryBase } from 'app/features/explore/QueryLibrary/types';
import { getDatasourceSrv } from 'app/features/plugins/datasource_srv';

export function useLoadUsers(userUIDs: string[] | undefined) {
  const userQtList = uniq(compact(userUIDs));
  // Skip the request when there are no users to resolve. The display-mapping endpoint rejects an
  // empty key list with a 400, which surfaces as an error toast in the empty saved-queries state.
  return useGetDisplayMappingQuery(
    userQtList.length > 0
      ? {
          key: userQtList,
        }
      : skipToken
  );
}

// A row's enriched metadata. When the datasource can't be resolved (e.g. it was deleted) the row still
// carries all DB-derived fields plus a `loadError`, so it stays renderable and deletable even without the
// datasource-derived metadata.
type MetadataValue = SavedQuery;

/**
 * Map metadata to saved queries we get from the DB.
 * @param savedQueries
 * @param userDataList
 * @param usersSettled whether the users lookup has settled (resolved, errored, or was skipped)
 */
export function useLoadQueryMetadata(
  savedQueries: SavedQueryBase[] | undefined,
  userDataList: ReturnType<typeof useLoadUsers>['data'],
  usersSettled: boolean
): AsyncState<MetadataValue[] | undefined> {
  return useAsync(async () => {
    // Wait until the list has loaded and the users lookup has settled. userDataList is legitimately
    // undefined when the users query was skipped (no users to resolve, e.g. an empty library), so we
    // gate on `usersSettled` instead of on userDataList being truthy — otherwise an empty list would
    // never resolve and callers relying on this value would spin forever.
    if (!savedQueries || !usersSettled) {
      return;
    }

    const display = userDataList?.display ?? [];

    const rowsPromises = savedQueries.map(async (savedQuery: SavedQueryBase): Promise<MetadataValue> => {
      const datasourceRef = savedQuery.targets[0]?.datasource;
      const query = savedQuery.targets[0];
      const extendedUserData = display.find(
        (user) => `${user?.identity.type}:${user?.identity.name}` === savedQuery.user?.uid
      );

      // Fields that don't depend on resolving the datasource, so they're shown on the row either way —
      // including when the datasource has been deleted and enrichment below throws.
      const base = {
        uid: savedQuery.uid,
        datasourceRef,
        createdAtTimestamp: savedQuery?.createdAtTimestamp || 0,
        query,
        title: savedQuery.title,
        description: savedQuery.description || '',
        tags: savedQuery.tags ?? [],
        isLocked: savedQuery.isLocked ?? false,
        isVisible: savedQuery.isVisible ?? false,
        user: {
          uid: savedQuery.user?.uid || '',
          displayName: extendedUserData?.displayName || savedQuery.user?.displayName || '',
          avatarUrl: extendedUserData?.avatarURL || savedQuery.user?.avatarUrl || '',
        },
      };

      try {
        const datasourceApi = await getDataSourceSrv().get(datasourceRef);
        return {
          ...base,
          datasourceName: datasourceApi?.name || '',
          datasourceType: getDatasourceSrv().getInstanceSettings(datasourceRef)?.meta.name || '',
          queryText: datasourceApi?.getQueryDisplayText?.(query),
        };
      } catch (error) {
        // Keep the row visible with its error so the user can still see and delete it — e.g. when its
        // datasource has been removed. Its stored type is still available even without the instance. This is
        // surfaced per-row in the UI (badge + detail banner), so it deliberately doesn't raise a global toast.
        const loadError = error instanceof Error ? error : new Error(extractErrorMessage(error, 'Unknown error'));
        return {
          ...base,
          datasourceType: datasourceRef?.type,
          loadError,
        };
      }
    });

    return Promise.all(rowsPromises);
  }, [savedQueries, userDataList, usersSettled]);
}

export const useGetNewSavedQuery = (query?: DataQuery, description?: string) => {
  const {
    data: userData,
    isLoading: isUserDataLoading,
    isError: isUserDataError,
  } = useLoadUsers(query ? [contextSrv.user.uid] : undefined);

  const {
    value: queryToAdd,
    loading: isQueryToAddLoading,
    error: queryToAddError,
  } = useAsync<() => Promise<SavedQuery | undefined>>(async () => {
    if (!query || isUserDataLoading) {
      return undefined;
    }
    const datasource = await getDataSourceSrv().get(query.datasource);
    const datasourceType = getDatasourceSrv().getInstanceSettings(query.datasource)?.meta.name || '';

    return {
      ...query,
      uid: undefined,
      query: query,
      queryText: datasource?.getQueryDisplayText?.(query),
      datasourceName: datasource.name,
      datasourceType,
      title: 'New query',
      description: description ?? '',
      tags: [],
      isVisible: true,
      isLocked: false,
      datasourceRef: query.datasource,
      createdAtTimestamp: new Date().getTime(),
      user: {
        uid: contextSrv.user?.uid || '',
        displayName: userData?.display[0]?.displayName || '',
        avatarUrl: userData?.display[0]?.avatarURL || '',
      },
    };
    // TODO: fix this dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, description, isUserDataLoading]);

  if (!query) {
    return {
      data: undefined,
      isLoading: false,
      isError: false,
    };
  }

  return {
    data: queryToAdd,
    isLoading: isQueryToAddLoading || isUserDataLoading,
    isError: Boolean(queryToAddError) || isUserDataError,
  };
};
